"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import DataTable, { FilterOption } from "../../components/commons/DataTable";
import Button from "../../components/commons/Button";
import type { Column } from "../../components/commons/DataTable";
import { getCommunityAnswers } from "../../services/webServices/community/answers/get";
import { patchCommunityAnswer } from "../../services/webServices/community/answers/patch";
import type { CommunityAnswer } from "../../services/webServices/community/answers/types";
import { Eye, ArrowRight2, ArrowLeft2 } from "iconsax-reactjs";

const STATUS_MAP = {
  0: { label: "منتشر نشده", color: "bg-gray-100 text-gray-700" },
  1: { label: "منتشر شده", color: "bg-green-100 text-green-700" },
  2: { label: "رد شده", color: "bg-red-100 text-red-700" },
};

const ROLE_MAP = {
  5: "معلم",
  2: "دانش‌آموز",
};

const FILE_URL = process.env.REACT_APP_File_URL || "";
const filterOptions: FilterOption[] = [
  { value: "0", label: "تعیین وضعیت نشده" },
  { value: "1", label: "تایید شده" },
  { value: "2", label: "رد شده" },
];

export default function SepehrJoAnswers() {
  const [answers, setAnswers] = useState<CommunityAnswer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedAnswer, setSelectedAnswer] =
    useState<CommunityAnswer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const [filterValue, setFilterValue] = useState<string>("");
  const pageSize = 10;

  // Fetch data
  const fetchAnswers = useCallback(
    async (page: number, search?: string, statusValue?: string) => {
      setLoading(true);
      try {
        const effectiveFilterValue =
          statusValue !== undefined ? statusValue : filterValue;
        const parsedStatus = effectiveFilterValue
          ? (Number(effectiveFilterValue) as 0 | 1 | 2)
          : null;

        const response = await getCommunityAnswers({
          params:
            page === 1 && !search && parsedStatus === null
              ? undefined
              : {
                  ...(page > 1 && { page }),
                  ...(search && { q: search }),
                  ...(parsedStatus !== null && { publish_status: parsedStatus }),
                },
        });

        setAnswers(response.results || []);
        setTotalItems(response.count || 0);
      } catch (error) {
        console.error("Error fetching answers:", error);
        setAnswers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [filterValue],
  );

  useEffect(() => {
    fetchAnswers(1);
  }, [fetchAnswers]);

  // Handle search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setCurrentPage(1);
      fetchAnswers(1, value);
    },
    [fetchAnswers],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchAnswers(page, searchValue);
    },
    [searchValue, fetchAnswers],
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (value: string) => {
      setFilterValue(value);
      setCurrentPage(1);
      setSearchValue("");
      fetchAnswers(1, "", value);
    },
    [fetchAnswers],
  );

  // Handle open modal
  const handleOpenModal = useCallback((answer: CommunityAnswer) => {
    setSelectedAnswer(answer);
    setIsModalOpen(true);
  }, []);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedAnswer(null), 300);
  }, []);

  // Handle image preview
  const handleImageClick = useCallback((imageSrc: string, index: number) => {
    setSelectedImageSrc(imageSrc);
    setSelectedImageIndex(index);
  }, []);

  const closeImagePreview = useCallback(() => {
    setSelectedImageSrc(null);
    setSelectedImageIndex(null);
  }, []);

  const imagePreviewItems = useMemo(
    () => selectedAnswer?.attachments || [],
    [selectedAnswer?.attachments],
  );

  // Handle patch answer
  const handlePatchAnswer = useCallback(
    async (id: number, publishStatus: 0 | 1 | 2) => {
      setModalLoading(true);
      try {
        const result = await patchCommunityAnswer({
          id,
          body: { publish_status: publishStatus },
        });

        if (result) {
          setSelectedAnswer(result);
          // Refresh answers list and close modal
          fetchAnswers(currentPage, searchValue);
          setTimeout(() => handleCloseModal(), 300);
        }
      } catch (error) {
        console.error("Error updating answer:", error);
      } finally {
        setModalLoading(false);
      }
    },
    [currentPage, searchValue, fetchAnswers, handleCloseModal],
  );

  // Define columns
  const columns: Column<CommunityAnswer>[] = useMemo(
    () => [
      {
        key: "id",
        header: "شماره پاسخ",
        accessor: (row) => (
          <a
            href={`https://sepehrjou.ir/answers/${row.id}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primaryVar-500 underline-offset-4 hover:underline"
          >
            {row.id}
          </a>
        ),
        sortable: true,
        width: "100px",
        align: "center",
      },
      {
        key: "user.full_name",
        header: "نام کاربر",
        accessor: (row) => row.user?.full_name || "-",
        width: "150px",
      },
      {
        key: "user.role",
        header: "نوع کاربر",
        accessor: (row) => {
          const roleKey = row.user?.role as keyof typeof ROLE_MAP;
          return ROLE_MAP[roleKey] || "-";
        },
        width: "120px",
      },
      {
        key: "created_at",
        header: "زمان انتشار",
        accessor: (row) => {
          const date = new Date(row.created_at || "");
          return date.toLocaleDateString("fa-IR");
        },
        sortable: true,
        width: "130px",
      },
      {
        key: "publish_status",
        header: "وضعیت انتشار",
        accessor: (row) => {
          const status =
            STATUS_MAP[row.publish_status as keyof typeof STATUS_MAP];
          return (
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                status?.color || "bg-gray-100 text-gray-700"
              }`}
            >
              {status?.label || "نامشخص"}
            </span>
          );
        },
        width: "130px",
        align: "center",
      },
      {
        key: "details",
        header: "جزییات",
        accessor: (row) => (
          <button
            onClick={() => handleOpenModal(row)}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-blue-600 transition-colors hover:bg-blue-100"
            title="مشاهده جزییات"
          >
            <Eye size="18" />
          </button>
        ),
        width: "100px",
        align: "right",
      },
    ],
    [handleOpenModal],
  );

  return (
    <div className="w-full" dir="rtl">
      <DataTable<CommunityAnswer>
        filterable
        filterPlaceholder="همه پاسخ‌ها"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}
        data={answers}
        columns={columns}
        title="مدیریت پاسخ‌های سپهرجو"
        searchable
        searchPlaceholder="جستجو بر اساس نام کاربر یا محتوا..."
        searchKeys={["content"] as (keyof CommunityAnswer)[]}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        loading={loading}
        loadingText="در حال بارگزاری پاسخ‌ها..."
        emptyText="پاسخی یافت نشد"
        pagination
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        serverSidePagination
      />

      {/* Modal */}
      {isModalOpen && selectedAnswer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={handleCloseModal}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                پاسخ #{selectedAnswer.id}
              </h2>
              <button
                onClick={handleCloseModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4">
              {/* User Info */}
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-1 lg:gap-3">
                  <div className="flex items-start gap-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 shrink-0">
                      <img
                        src={`${FILE_URL}${selectedAnswer.user?.avatar || ""}`}
                        alt={selectedAnswer.user?.full_name || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <p className="text-sm font-bold text-gray-800">
                        {selectedAnswer.user?.full_name || "-"}
                      </p>
                      <div className="hidden lg:flex items-center gap-2">
                        <span className="inline-block rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium">
                          پاسخ به سوال #{typeof selectedAnswer.question === 'object' ? selectedAnswer.question.id : selectedAnswer.question}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-end gap-3 text-sm sm:flex-row sm:items-center sm:gap-3">
                    <span className="text-gray-500 text-xs whitespace-nowrap inline-block">
                      {selectedAnswer.created_at ? new Date(selectedAnswer.created_at).toLocaleDateString(
                        "fa-IR",
                      ) : "-"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 lg:hidden">
                  <span className="inline-block rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium">
                    پاسخ به سوال #{typeof selectedAnswer.question === 'object' ? selectedAnswer.question.id : selectedAnswer.question}
                  </span>
                </div>
              </div>

              {/* Parent Answer or Question */}
              {selectedAnswer.parent && (
                <div className="border-r-2 border-blue-200 pr-3">
                  <p className="text-xs text-gray-500 mb-2">پاسخ به:</p>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 shrink-0">
                      <img
                        src={`${FILE_URL}${selectedAnswer.parent.user?.avatar || ""}`}
                        alt={selectedAnswer.parent.user?.full_name || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-700">
                        {selectedAnswer.parent.user?.full_name || "-"}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {selectedAnswer.parent.content}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!selectedAnswer.parent && typeof selectedAnswer.question === 'object' && (
                <div className="border-r-2 border-purple-200 pr-3">
                  <p className="text-xs text-gray-500 mb-2">سوال اصلی:</p>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 shrink-0">
                      <img
                        src={`${FILE_URL}${selectedAnswer.question.user?.avatar || ""}`}
                        alt={selectedAnswer.question.user?.full_name || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-700">
                        {selectedAnswer.question.user?.full_name || "-"}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {selectedAnswer.question.content}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Answer Content */}
              <div className="border-r-2 border-green-200 pr-3">
                <p className="text-xs text-gray-500 mb-2">پاسخ این کاربر:</p>
                <p className="text-xs text-gray-900 font-normal text-right">
                  {selectedAnswer.content}
                </p>
              </div>

              {/* Attachments */}
              {selectedAnswer.attachments &&
                selectedAnswer.attachments.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedAnswer.attachments.map((attachment, index) => (
                      <button
                        key={attachment.id || index}
                        type="button"
                        onClick={() =>
                          handleImageClick(attachment.file_content, index)
                        }
                        className="w-[88px] h-[88px] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity"
                        aria-label="نمایش تصویر در حالت تمام صفحه"
                      >
                        <img
                          src={`${FILE_URL}${attachment.file_content}`}
                          alt={`پیوست ${index + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                    ))}
                  </div>
                )}

              {/* Action Buttons */}
              <div className="flex items-end justify-end gap-3 pt-4 mt-4">
                <Button
                  variant="primary"
                  style="solid"
                  size="md"
                  text="تایید پاسخ"
                  loading={modalLoading}
                  disabled={modalLoading}
                  onClick={() => handlePatchAnswer(selectedAnswer.id, 1 as 0 | 1 | 2)}
                />
                <Button
                  variant="primary"
                  style="solid"
                  size="md"
                  text="رد پاسخ"
                  loading={modalLoading}
                  disabled={modalLoading}
                  onClick={() => handlePatchAnswer(selectedAnswer.id, 2 as 0 | 1 | 2)}
                  customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {selectedImageSrc && (
        <div
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <div
            className="relative flex h-[min(82vh,720px)] w-full max-w-5xl items-center justify-center rounded-2xl px-12 py-14 sm:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {imagePreviewItems.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setSelectedImageIndex((prev) =>
                    prev === null
                      ? 0
                      : (prev - 1 + imagePreviewItems.length) %
                        imagePreviewItems.length,
                  )
                }
                className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/45 text-white sm:right-5"
              >
                <ArrowRight2 size={20} />
              </button>
            )}
            {imagePreviewItems.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setSelectedImageIndex((prev) =>
                    prev === null ? 0 : (prev + 1) % imagePreviewItems.length,
                  )
                }
                className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/45 text-white sm:left-5"
              >
                <ArrowLeft2 size={20} />
              </button>
            )}
            <button
              type="button"
              onClick={closeImagePreview}
              className="absolute top-4 right-4 z-10 text-white text-sm border border-white/40 rounded-md px-3 py-1 bg-black/35"
            >
              بستن
            </button>
            <img
              src={`${FILE_URL}${selectedImageSrc}`}
              alt="answer-attachment-fullscreen"
              className="h-full max-h-full w-full max-w-full object-contain rounded-xl"
            />
            {imagePreviewItems.length > 1 && selectedImageIndex !== null && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                {selectedImageIndex + 1} / {imagePreviewItems.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
